import React, { FC, useMemo } from 'react'

import { graphql } from 'react-relay'
import {
  Location,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { FormikHelpers } from 'formik'

import { YStack, XStack } from 'tamagui'

import { H2, Paragraph, Text } from '@newComponents/Text'
import { Link } from '@newComponents/Link'
import { ErrorText } from '@newComponents/Form/FormField'

import { useMutation } from '@data/useMutation'

import {
  FormValues as SignUpFormValues,
  SignUpForm,
} from '@forms/User/SignUpForm'

import { addBreadcrumb } from '@sentry/react'

import { SignUpScreenContentSignUpMutation } from './__generated__/SignUpScreenContentSignUpMutation.graphql'
import { SignUpScreenContentSignInMutation } from './__generated__/SignUpScreenContentSignInMutation.graphql'

export const SignUpScreenContent: FC = () => {
  const signUpMutation = useMutation<SignUpScreenContentSignUpMutation>(graphql`
    mutation SignUpScreenContentSignUpMutation(
      $input: SignUpUserMutationInput!
    ) {
      signUpUser(input: $input) {
        __typename
      }
    }
  `)
  const generateSignInMutation =
    useMutation<SignUpScreenContentSignInMutation>(graphql`
      mutation SignUpScreenContentSignInMutation(
        $input: GenerateSignInTokenMutationInput!
      ) {
        generateSignInToken(input: $input) {
          __typename
        }
      }
    `)

  const navigate = useNavigate()
  const location = useLocation()

  const [searchParams] = useSearchParams()

  const routerOptions = useMemo(
    () => ({
      state: {
        backgroundLocation: location.state?.backgroundLocation as Location,
        authRedirectPath:
          searchParams.get('authRedirectPath') ||
          (location.state?.authRedirectPath as string),
      },
    }),
    [location, searchParams.get('authRedirectPath')]
  )

  const handleSubmit = async (
    values: SignUpFormValues,
    actions: FormikHelpers<SignUpFormValues>
  ) => {
    const signUpResult = await signUpMutation({
      variables: {
        input: {
          email: values.email,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          authRedirectPath: routerOptions.state?.authRedirectPath,
        },
      },
    })

    switch (signUpResult.signUpUser.__typename) {
      case 'SignUpUserMutationSuccessResult':
        navigate('/auth/code', routerOptions)
        break
      case 'SignUpUserMutationDuplicateEmailResult': {
        const signInResult = await generateSignInMutation({
          variables: { input: { email: values.email } },
        })

        switch (signInResult.generateSignInToken.__typename) {
          case 'GenerateSignInTokenMutationSuccess':
            navigate('/auth/code', {
              state: { ...routerOptions.state, duplicateSignUp: true },
            })
            break
          // prevent sign-up/sign-in infinite loop
          case 'GenerateSignInTokenMutationUnknownEmailError':
          default:
            actions.setStatus(
              'There was an error signing you in. Please try again or contact us at hi@guild.host for assistance'
            )
        }
        break
      }
      case 'SignUpUserMutationUnknownErrorResult':
      default: {
        // TODO: Display error to user
        console.error('ERROR SIGNING UP', signUpResult.signUpUser)

        addBreadcrumb({
          category: 'auth.signup',
          message: JSON.stringify(signUpResult.signUpUser),
          level: 'debug',
        })
      }
    }
  }

  return (
    <YStack gap='$6'>
      <YStack gap='$6'>
        {location.state?.signInError === 'unknown-email' && (
          <YStack gap='$4'>
            <Text size='$4'>We couldn't locate that email</Text>

            <Text size='$4'>Do you want to sign-up?</Text>
          </YStack>
        )}

        <SignUpForm onSubmit={handleSubmit} />
      </YStack>
    </YStack>
  )
}

export default SignUpScreenContent
